/**
 * Formik-bound form fields.
 *
 * The stack mandates **Formik + Yup** (plans §7). docs/06 §4.1 predates that decision and still
 * says react-hook-form + zod; the operative requirements survive the swap and are implemented
 * here:
 *   • Every input is LABELLED, and its error is wired via `aria-describedby` + `aria-invalid`.
 *   • Validation on blur first, then on change once a field has erred (Formik:
 *     `validateOnBlur` + `validateOnChange`, with the error shown only once `touched`).
 *   • Errors are translated, specific, and placed UNDER the field.
 *   • Units render INLINE (kg, $, шт.), not in placeholder text.
 *   • Numeric inputs carry `inputMode` so mobile keyboards behave.
 */
import * as React from 'react';
import { useField, useFormikContext } from 'formik';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@most/ui';
import { Input, Textarea, cn } from './primitives';

// ── Field shell ─────────────────────────────────────────────────────────────────────────────
function FieldShell({
  name,
  label,
  hint,
  required,
  error,
  children,
  className,
  id,
}: {
  name: string;
  label?: string;
  hint?: React.ReactNode;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  className?: string;
  id: string;
}) {
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;

  return (
    <div className={cn('space-y-1.5', className)}>
      {label ? (
        <label htmlFor={id} className="block text-sm font-medium text-foreground/80">
          {label}
          {required ? (
            <span className="ml-0.5 text-red-500" aria-hidden>
              *
            </span>
          ) : null}
        </label>
      ) : null}

      {children}

      {hint && !error ? (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}

      {error ? (
        // The error is announced, not just coloured — status is never conveyed by colour alone.
        <p id={errId} className="text-xs font-medium text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      <input type="hidden" data-field={name} />
    </div>
  );
}

/** Shared plumbing: pull Formik state and derive the a11y wiring for one field. */
function useFieldA11y(name: string, label?: string, hint?: React.ReactNode) {
  const [field, meta] = useField(name);
  const reactId = React.useId();
  const id = `f-${name.replace(/[^\w-]/g, '-')}-${reactId}`;

  // Show the error only once the user has left the field (or tried to submit) — erroring at the
  // first keystroke of an empty required field is hostile.
  const error = meta.touched && meta.error ? meta.error : undefined;

  const describedBy =
    [error ? `${id}-err` : null, hint && !error ? `${id}-hint` : null].filter(Boolean).join(' ') ||
    undefined;

  return { field, meta, id, error, describedBy, label };
}

// ── Text ────────────────────────────────────────────────────────────────────────────────────
export function TextField({
  name,
  label,
  hint,
  required,
  className,
  suffix,
  ...props
}: {
  name: string;
  label?: string;
  hint?: React.ReactNode;
  required?: boolean;
  className?: string;
  /** Unit rendered INLINE inside the field (kg, $, шт.) — never as placeholder text. */
  suffix?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name' | 'id'>) {
  const { field, id, error, describedBy } = useFieldA11y(name, label, hint);

  return (
    <FieldShell
      name={name}
      id={id}
      label={label}
      hint={hint}
      required={required}
      error={error}
      className={className}
    >
      <div className="relative">
        <Input
          id={id}
          {...field}
          {...props}
          value={field.value ?? ''}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
          className={cn(suffix && 'pr-12')}
        />
        {suffix ? (
          <span
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground"
            aria-hidden
          >
            {suffix}
          </span>
        ) : null}
      </div>
    </FieldShell>
  );
}

/**
 * Numeric text field: `inputmode` for mobile keyboards, unit inline.
 *
 * Stays `type="text"` (values are strings — see wizard/schema.ts), but rejects any keystroke or
 * paste that is not a number: digits only, or digits with one `.`/`,` separator when `decimal`.
 */
export function NumberField(
  props: React.ComponentProps<typeof TextField> & { decimal?: boolean },
) {
  const { decimal, onChange, ...rest } = props;
  const [, , helpers] = useField(props.name);
  const allowed = decimal ? /^\d*[.,]?\d*$/ : /^\d*$/;

  return (
    <TextField
      {...rest}
      type="text"
      inputMode={decimal ? 'decimal' : 'numeric'}
      autoComplete="off"
      onChange={(e) => {
        const next = e.target.value.replace(/\s/g, '');
        if (!allowed.test(next)) return;
        void helpers.setValue(next);
        onChange?.(e);
      }}
    />
  );
}

export function TextAreaField({
  name,
  label,
  hint,
  required,
  className,
  rows = 4,
  ...props
}: {
  name: string;
  label?: string;
  hint?: React.ReactNode;
  required?: boolean;
  className?: string;
} & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'name' | 'id'>) {
  const { field, id, error, describedBy } = useFieldA11y(name, label, hint);

  return (
    <FieldShell
      name={name}
      id={id}
      label={label}
      hint={hint}
      required={required}
      error={error}
      className={className}
    >
      <Textarea
        id={id}
        rows={rows}
        {...field}
        {...props}
        value={field.value ?? ''}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
      />
    </FieldShell>
  );
}

/**
 * SelectField — Formik-bound wrapper over the SHARED custom select (`@most/ui`), not a native
 * <select>.
 *
 * The native element was styleable everywhere except the one place it mattered: the option rows,
 * which the OS draws. On macOS that meant a system-green highlight on a warm-slate form — a
 * control that visibly did not belong to this design system. `@most/ui`'s Select renders those
 * rows itself (portalled, so a popover inside a clipped or sticky container is neither cut off
 * nor painted under its neighbour) and pays back what the native element gave for free: the
 * combobox/listbox/option ARIA quartet, arrow-key + Home/End movement, typeahead, and
 * Escape-closes-and-restores-focus. See that component's header for the full accounting.
 *
 * Two consequences for the binding, both deliberate:
 *   • It is controlled by `value` + `onValueChange`, not a DOM change event, so Formik is driven
 *     through `setFieldValue` rather than `field.onChange`.
 *   • It has no blur event to hang "validate on blur" off, so committing a value also marks the
 *     field touched — which is the same moment the user finishes with the control.
 *
 * `id` and `aria-describedby` go on <Select>, which forwards them down to the trigger BUTTON.
 * Passing them to a context provider that renders no DOM would leave the <label htmlFor> pointing
 * at nothing; the shared component accepts them at this level precisely to close that trap.
 */
export function SelectField({
  name,
  label,
  hint,
  required,
  className,
  options,
  placeholder,
  disabled,
}: {
  name: string;
  label?: string;
  hint?: React.ReactNode;
  required?: boolean;
  className?: string;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  disabled?: boolean;
}) {
  const { field, id, error, describedBy } = useFieldA11y(name, label, hint);
  const { setFieldValue, setFieldTouched } = useFormikContext<unknown>();

  return (
    <FieldShell
      name={name}
      id={id}
      label={label}
      hint={hint}
      required={required}
      error={error}
      className={className}
    >
      <Select
        id={id}
        value={field.value ?? ''}
        onValueChange={(next) => {
          void setFieldValue(name, next);
          void setFieldTouched(name, true);
        }}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value} textValue={o.label}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldShell>
  );
}
